/**
 * useReadingScale - the reader's document text size, bound to a component.
 *
 * Seeded synchronously from the local cache so the first frame is already the
 * right size, then reconciled with the server once. See
 * `services/preferences/readingScale` for why it is one global per-user value
 * rather than one per document.
 */

import { useCallback, useEffect, useState } from 'react';
import { useAppSelector, RootState } from '../../store';
import {
  DEFAULT_READING_SCALE,
  hydrateReadingScale,
  loadReadingScale,
  readingScaleLabel,
  READING_SCALES,
  saveReadingScale,
  stepReadingScale,
} from '../../services/preferences/readingScale';

export interface ReadingScaleApi {
  scale: number;
  /** `110%`, for the control's readout. */
  label: string;
  zoomIn: () => void;
  zoomOut: () => void;
  reset: () => void;
  canZoomIn: boolean;
  canZoomOut: boolean;
  isDefault: boolean;
}

export const useReadingScale = (): ReadingScaleApi => {
  const userId = useAppSelector((s: RootState) => s.user.userInfo?.UserUID) || 'anon';
  // Lazy initialiser, not an effect: an effect would paint the default first
  // and then jump to the stored size.
  const [scale, setScale] = useState<number>(() => loadReadingScale(userId));

  /* The server is the source of truth across devices; the cache is what makes
     the first frame right. A failed read leaves the cached value alone. */
  useEffect(() => {
    let cancelled = false;
    void hydrateReadingScale(userId).then((remote) => {
      if (!cancelled && remote != null) setScale(remote);
    });
    return () => { cancelled = true; };
  }, [userId]);

  const commit = useCallback((next: number) => {
    setScale(next);
    saveReadingScale(userId, next);
  }, [userId]);

  return {
    scale,
    label: readingScaleLabel(scale),
    zoomIn: () => commit(stepReadingScale(scale, 1)),
    zoomOut: () => commit(stepReadingScale(scale, -1)),
    reset: () => commit(DEFAULT_READING_SCALE),
    canZoomIn: scale < READING_SCALES[READING_SCALES.length - 1],
    canZoomOut: scale > READING_SCALES[0],
    isDefault: scale === DEFAULT_READING_SCALE,
  };
};

export default useReadingScale;
